import React, { useState, useEffect, useMemo } from "react"
import { VStack, HStack, Text, Icon } from "@chakra-ui/react"
import { useQuery } from "@apollo/client"
import {
  GetMonitorsDocument,
  GetMonitoringInstancesDocument,
  MonitoringFeatureInput,
} from "@hydrologiq/gql/graphql"
import { Dialog as ChakraDialog } from "@chakra-ui/react"
import { Field } from "@hydrologiq/shared/chakra/Field"
import { NativeSelectField, NativeSelectRoot } from "@hydrologiq/shared/chakra/NativeSelect"
import { Switch } from "@hydrologiq/shared/chakra/Switch"
import { CancelButton, ConfirmButton } from "@hydrologiq/shared/content/components/Button"
import { Section } from "@hydrologiq/shared/chakra/custom/Section"
import Popover from "@hydrologiq/shared/chakra/Popover"
import { Alert } from "@hydrologiq/shared/chakra/Alert"
import { FaTriangleExclamation } from "react-icons/fa6"
import MonitoringFeatureConfirmationDialog from "./MonitoringFeatureConfirmationDialog"
import InputConflictWarningDialog from "./InputConflictWarningDialog"
import VolumePressureInputs from "./VolumePressureInputs"
import { AssetKind, Asset } from "@hydrologiq/gql-experience/graphql"

export interface AssetMonitoringActivationPopoverProps {
  open: boolean
  onClose: () => void
  onActivate: (
    monitoringInstanceId: string,
    features?: MonitoringFeatureInput,
    volume?: number | null,
    min_pressure?: number | null
  ) => void
  assetId: string
  asset?: Asset
  loading?: boolean
}

interface MonitorOption {
  id: string
  name: string
  kind: string
}

interface InstanceOption {
  id: string
  name: string
  installationName?: string | null
}

// Dependency validation system
interface MonitoringDependency {
  id: string
  name: string
  check: (asset?: Asset) => boolean
  errorMessage: string
  helpText: string
}

// Volume dependency for weight calculation
const VOLUME_DEPENDENCY: MonitoringDependency = {
  id: "volume",
  name: "Asset Volume",
  check: (asset) => asset?.volume !== null && asset?.volume !== undefined && asset.volume > 0,
  errorMessage: "No volume information specified for this asset.",
  helpText:
    "Volume information is required to calculate hydrogen weight. Please specify the volume information in the asset information section below to enable weight calculation.",
}

const AssetMonitoringActivationPopover: React.FC<AssetMonitoringActivationPopoverProps> = ({
  open,
  onClose,
  onActivate,
  asset,
  loading = false,
}) => {
  const [selectedMonitorId, setSelectedMonitorId] = useState<string>("")
  const [selectedInstanceId, setSelectedInstanceId] = useState<string>("")

  // Monitoring features state
  const [weightCalculationEnabled, setWeightCalculationEnabled] = useState<boolean>(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false)

  // Volume and pressure state for dynamic_storage assets or when volume is missing
  const [volume, setVolume] = useState<string>(asset?.volume ? asset.volume.toString() : "")
  const [minPressure, setMinPressure] = useState<string>(asset?.min_pressure ? asset.min_pressure.toString() : "")

  // Check if this is a dynamic_storage asset
  const isDynamicStorage = asset?.kind && asset.kind === AssetKind.DynamicStorage
  const isVolumeDependencyMet = useMemo(() => {
    // Check if volume dependency is met from asset OR from user input
    const assetHasVolume = VOLUME_DEPENDENCY.check(asset)
    const userProvidedVolume = volume.trim() && parseFloat(volume.trim()) > 0

    // For dynamic_storage, require both volume and min_pressure from user input
    // For other assets, check asset volume OR user-provided volume
    return (
      (isDynamicStorage
        ? userProvidedVolume && minPressure.trim() && parseFloat(minPressure.trim()) >= 0
        : assetHasVolume || userProvidedVolume) || false
    )
  }, [asset, volume, minPressure, isDynamicStorage])

  // Dependency validation state
  const [volumeDependencyMet, setVolumeDependencyMet] = useState<boolean>(false)
  const [showVolumeWarning, setShowVolumeWarning] = useState<boolean>(false)
  const [highlightWarning, setHighlightWarning] = useState<boolean>(false)

  // Show volume/pressure inputs if volume warning is active OR asset is dynamic_storage
  const showVolumePressureInputs = useMemo(() => {
    return showVolumeWarning || isDynamicStorage
  }, [showVolumeWarning, isDynamicStorage])


  const hideMonitoringFeaturesSection = useMemo(() => {
    return !(asset?.kind && [AssetKind.DynamicStorage, AssetKind.TubeTrailer, AssetKind.ManifoldCylinderPallet, AssetKind.TowTractor, AssetKind.DualFuelVehicle].includes(asset.kind))
  }, [asset])

  // Section visibility - computed values, not state
  const showMonitoringFeaturesSection = useMemo(() => {
    return (showVolumeWarning || !isVolumeDependencyMet)
  }, [])

  const showAssetInformationSection = useMemo(() => {
    return isDynamicStorage || !isVolumeDependencyMet
  }, [])

  // Conflict detection state
  const [showConflictWarning, setShowConflictWarning] = useState<boolean>(false)
  const [conflictingAssets, setConflictingAssets] = useState<Array<{ id: string; name: string }>>([])
  const [pendingInstanceId, setPendingInstanceId] = useState<string>("")

  // Query for available monitors
  const { data: monitorsData, error: monitorsError } = useQuery(GetMonitorsDocument, {
    skip: !open,
  })

  // Query for monitoring instances (only when a monitor is selected)
  const { data: instancesData, error: instancesError } = useQuery(GetMonitoringInstancesDocument, {
    variables: { monitor: selectedMonitorId },
    skip: !selectedMonitorId || !open,
  })

  // Reset instance selection when monitor changes
  useEffect(() => {
    setSelectedInstanceId("")
  }, [selectedMonitorId])

  // Validate dependencies when asset changes or volume/pressure inputs change
  useEffect(() => {
    setVolumeDependencyMet(isVolumeDependencyMet)
    setShowVolumeWarning(!isVolumeDependencyMet)
  }, [asset, volume, minPressure, isDynamicStorage])

  useEffect(() => {
    setWeightCalculationEnabled(volumeDependencyMet)
  }, [volumeDependencyMet])

  // Reset selections when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedMonitorId("")
      setSelectedInstanceId("")
      setShowConfirmDialog(false)
      setHighlightWarning(false)

      // Reset conflict state
      setShowConflictWarning(false)
      setConflictingAssets([])
      setPendingInstanceId("")

      // Reset volume/pressure inputs
      setVolume(asset?.volume ? asset.volume.toString() : "")
      setMinPressure(asset?.min_pressure ? asset.min_pressure.toString() : "")

      // Validate dependencies - will be updated by the useEffect when volume/pressure change
      const assetHasVolume = VOLUME_DEPENDENCY.check(asset)
      setShowVolumeWarning(!assetHasVolume && !isDynamicStorage)

      // Only enable weight calculation if asset already has volume (user input will be handled by useEffect)
      setWeightCalculationEnabled(assetHasVolume)
    }
  }, [open, asset])

  // Process monitors data
  const monitorOptions: MonitorOption[] =
    monitorsData?.monitors?.map((monitor) => ({
      id: monitor.id,
      name: monitor.name,
      kind: monitor.kind,
    })) || []

  // Process instances data
  const instanceOptions: InstanceOption[] =
    instancesData?.monitoringInstances?.map((instance) => ({
      id: instance.id,
      name: instance.name,
      installationName: instance.installation?.name,
    })) || []

  const handleMonitorChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMonitorId(event.target.value)
  }

  const handleInstanceChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const instanceId = event.target.value

    if (!instanceId) {
      setSelectedInstanceId("")
      return
    }

    // Find the selected instance to check for conflicts
    const instanceData = instancesData?.monitoringInstances?.find((instance) => instance.id === instanceId)

    // Check if this instance has active assignments (conflicts)
    const activeAssignments =
      instanceData?.assignments?.filter((assignment) => assignment.active && assignment.status === "active") || []

    if (activeAssignments.length > 0) {
      // Show conflict warning
      setConflictingAssets(
        activeAssignments.map((assignment) => ({
          id: assignment.asset.id,
          name: assignment.asset.name,
        }))
      )
      setPendingInstanceId(instanceId)
      setShowConflictWarning(true)
    } else {
      // No conflicts, proceed normally
      setSelectedInstanceId(instanceId)
    }
  }

  const handleActivateClick = () => {
    if (selectedInstanceId) {

      const volumeNum = volume.trim() ? parseFloat(volume.trim()) : null
      const minPressureNum = minPressure.trim() ? parseFloat(minPressure.trim()) : null
      // Validate volume/pressure for dynamic_storage assets or when volume is missing
      if (weightCalculationConditionsMet && weightCalculationEnabled) {

        if (isDynamicStorage) {
          // For dynamic_storage, both are required
          if (volumeNum === null && minPressureNum === null) {
            return
          }
          if ((volumeNum !== null && volumeNum <= 0) || (minPressureNum !== null && minPressureNum < 0)) {
            return
          }
        } else {
          // For other assets with missing volume, at least volume is required
          if (volumeNum === null || volumeNum <= 0) {
            return
          }
        }
      }

      const features: MonitoringFeatureInput = {
        weight_calculation: weightCalculationEnabled,
      }

      onActivate(selectedInstanceId, features, volumeNum, minPressureNum)
    }
  }

  // Handle weight calculation toggle
  const handleWeightCalculationToggle = (checked: boolean) => {
    if (!checked) {
      // Show confirmation dialog when trying to disable (but don't change state yet)
      setShowConfirmDialog(true)
    } else {
      // Check dependencies before enabling - volumeDependencyMet is updated by useEffect
      if (!volumeDependencyMet) {
        // Highlight the warning message and prevent enabling
        setHighlightWarning(true)
        // Remove highlight after 3 seconds
        setTimeout(() => setHighlightWarning(false), 3000)
        return
      }
      // Allow enabling if dependencies are met
      setWeightCalculationEnabled(true)
    }
  }

  // Disable weight calculation if volume dependency is no longer met
  useEffect(() => {
    if (!volumeDependencyMet && weightCalculationEnabled) {
      setWeightCalculationEnabled(false)
    }
  }, [volumeDependencyMet, weightCalculationEnabled])

  // Handle confirmation of disabling weight calculation
  const handleConfirmDisable = () => {
    setWeightCalculationEnabled(false)
  }

  // Handle conflict resolution
  const handleConflictConfirm = () => {
    // User confirmed they want to use the input despite conflicts
    setSelectedInstanceId(pendingInstanceId)
    setPendingInstanceId("")
    setConflictingAssets([])
    setShowConflictWarning(false)
  }

  const handleConflictCancel = () => {
    // User canceled, reset the selection
    setSelectedInstanceId("")
    setPendingInstanceId("")
    setConflictingAssets([])
    setShowConflictWarning(false)
  }

  const weightCalculationConditionsMet = useMemo(() => {
    return hideMonitoringFeaturesSection || !weightCalculationEnabled || (weightCalculationEnabled && volumeDependencyMet && parseFloat(volume.trim()) > 0)
  }, [hideMonitoringFeaturesSection, weightCalculationEnabled, volumeDependencyMet, volume])


  // Form validation: check if all required fields are filled
  const isFormValid =
    selectedMonitorId &&
    selectedInstanceId &&
    weightCalculationConditionsMet

  return (
    <>
      <ChakraDialog.Root open={open} onOpenChange={() => { }}>
        <ChakraDialog.Backdrop />
        <ChakraDialog.Positioner>
          <ChakraDialog.Content maxWidth="md" data-testid="activation-popover">
            <ChakraDialog.Header>
              <ChakraDialog.Title>Activate monitoring</ChakraDialog.Title>
            </ChakraDialog.Header>

            <ChakraDialog.Body>
              <VStack gap={4} align="stretch">
                {/* Monitor Selection */}
                <Field label="Monitor">
                  {monitorsError ? (
                    <Text color="red.500">Failed to load monitors</Text>
                  ) : (
                    <NativeSelectRoot>
                      <NativeSelectField
                        placeholder="Select a monitor"
                        value={selectedMonitorId}
                        onChange={handleMonitorChange}
                        data-testid="monitor-select"
                      >
                        {monitorOptions.map((monitor) => (
                          <option key={monitor.id} value={monitor.id}>
                            {monitor.name} ({monitor.kind})
                          </option>
                        ))}
                      </NativeSelectField>
                    </NativeSelectRoot>
                  )}
                </Field>

                {/* Input Selection */}
                <Field label="Input">
                  {!selectedMonitorId ? (
                    <Text color="gray.500" fontSize="sm">
                      Please select a monitor first
                    </Text>
                  ) : instancesError ? (
                    <Text color="red.500">Failed to load monitoring instances</Text>
                  ) : instanceOptions.length === 0 ? (
                    <Text color="gray.500" fontSize="sm">
                      No monitoring instances available for this monitor
                    </Text>
                  ) : (
                    <NativeSelectRoot>
                      <NativeSelectField
                        placeholder="Select an input"
                        value={selectedInstanceId}
                        onChange={handleInstanceChange}
                        data-testid="instance-select"
                      >
                        {instanceOptions.map((instance) => (
                          <option key={instance.id} value={instance.id}>
                            {instance.name}
                          </option>
                        ))}
                      </NativeSelectField>
                    </NativeSelectRoot>
                  )}
                </Field>

                {/* Monitoring Features Section */}
                {!hideMonitoringFeaturesSection && <Section
                  heading={
                    <HStack gap={2} align="center">
                      <Text>Monitoring features</Text>
                      {showVolumeWarning && !isDynamicStorage && (
                        <Icon as={FaTriangleExclamation} color="orange.500" boxSize={4} data-testid="warning-icon" />
                      )}
                    </HStack>
                  }
                  collapsible={true}
                  separator={false}
                  defaultOpen={showMonitoringFeaturesSection}
                >
                  <VStack gap={3} align="stretch">
                    <Text fontSize="sm" color="fg.muted">
                      Configure advanced monitoring features
                    </Text>

                    {/* Volume dependency warning */}
                    {showVolumeWarning && !isDynamicStorage && (
                      <Alert
                        status="warning"
                        title={VOLUME_DEPENDENCY.errorMessage}
                        icon={<FaTriangleExclamation />}
                        data-testid="volume-warning-alert"
                        borderColor={highlightWarning ? "orange.400" : undefined}
                        borderWidth={highlightWarning ? "2px" : undefined}
                        transition="border-color 0.3s ease"
                      >
                        <Text fontSize="sm">{VOLUME_DEPENDENCY.helpText}</Text>
                      </Alert>
                    )}
                    <HStack justify="space-between" align="center">
                      <HStack gap={2} align="center" flex={1}>
                        <Text fontSize="sm">Weight calculation</Text>
                        <Popover iconType="info" placement="top-end" size="xs">
                          <VStack align="start" gap={2} minWidth="280px" padding={4}>
                            <Text fontSize="sm">
                              Enable fuel level calculation using pressure and temperature measurements from your
                              monitoring device.
                            </Text>
                          </VStack>
                        </Popover>
                      </HStack>
                      <Switch
                        checked={weightCalculationEnabled}
                        onCheckedChange={({ checked }) => {
                          // Only handle change if the switch is not disabled
                          if (!loading && volumeDependencyMet) {
                            handleWeightCalculationToggle(checked)
                          }
                        }}
                        disabled={loading || !volumeDependencyMet}
                        data-testid="weight-calculation-toggle"
                      />
                    </HStack>
                  </VStack>
                </Section>}

                {isDynamicStorage && <Section
                  heading="Asset information"
                  collapsible={true}
                  separator={false}
                  defaultOpen={showAssetInformationSection}
                >
                  <VStack gap={3} align="stretch">
                    <Text fontSize="sm">Asset information</Text>

                    {/* Volume and Pressure Inputs Section */}
                    <VStack gap={3} align="stretch" fontSize="sm">
                      <VolumePressureInputs
                        defaultVolume={volume ? parseFloat(volume) : null}
                        defaultMinPressure={minPressure ? parseFloat(minPressure) : null}
                        onChange={(volume, minPressure) => {
                          setVolume(volume.toString())
                          setMinPressure(minPressure.toString())
                        }}
                        required={isDynamicStorage}
                        helperText={
                          isDynamicStorage
                            ? "These values will be set on the asset and used for weight and forecast calculations."
                            : "Specify volume and pressure information for this asset."
                        }
                      />
                    </VStack>
                  </VStack>
                </Section>}
              </VStack>
            </ChakraDialog.Body>

            <ChakraDialog.Footer>
              <HStack gap={3}>
                <CancelButton onClick={onClose} disabled={loading} />
                <ConfirmButton onClick={handleActivateClick} disabled={!isFormValid || loading} loading={loading} />
              </HStack>
            </ChakraDialog.Footer>
          </ChakraDialog.Content>
        </ChakraDialog.Positioner>
      </ChakraDialog.Root>

      {/* Monitoring Feature Confirmation Dialog */}
      <MonitoringFeatureConfirmationDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmDisable}
        featureName="Weight Calculation"
        loading={loading}
      />

      {/* Input Conflict Warning Dialog */}
      <InputConflictWarningDialog
        open={showConflictWarning}
        onClose={() => setShowConflictWarning(false)}
        onConfirm={handleConflictConfirm}
        onCancel={handleConflictCancel}
        currentAssetName={asset?.name || "this asset"}
        conflictingAssets={conflictingAssets}
        loading={loading}
      />
    </>
  )
}

export default AssetMonitoringActivationPopover
